import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';
import RecaptchaNotice from './RecaptchaNotice';

/**
 * Tab 1: Upload CV
 * Captures: Email, Position, Employment Type, CV
 * Styled to match original ASTUDIO form exactly
 */
const Tab1_UploadCV = ({ formData, onChange, onNext, errors, loading, loadingMessage, progress, positions: positionsProp }) => {
  const [cvFile, setCvFile] = useState(null);

  // Format positions for SearchableSelect
  const positions = positionsProp ? positionsProp.map(p => ({ value: p.title, label: p.title })) : [];
  const loadingPositions = !positionsProp || positionsProp.length === 0;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        return;
      }
      setCvFile(file);
      onChange({ target: { name: 'cv', value: file, type: 'file', files: [file] } });
    }
  };

  const removeFile = () => {
    setCvFile(null);
    onChange({ target: { name: 'cv', value: null } });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (onNext) {
      onNext();
    }
  };

  return (
    <div className="tab-content">
      <div className="astudio-form-container">
        {/* Row 1: Email and Position */}
        <div className="ast-contact-form-row">
          {/* Email */}
          <div className="form-field">
            <label className="req">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={onChange}
              placeholder="Your email address here"
              className={errors.email ? 'field-error-input' : ''}
              autoComplete="email"
              disabled={loading}
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>

          {/* Position */}
          <div className="form-field">
            <label className="req">Position</label>
            {loadingPositions ? (
              <div style={{ padding: '10px 15px', color: '#6c757d' }}>
                Loading positions...
              </div>
            ) : (
              <SearchableSelect
                name="position"
                value={formData.position || ''}
                onChange={onChange}
                options={positions}
                placeholder="Please select a position"
                error={errors.position}
                disabled={loading}
                enableSmartSearch={true}
              />
            )}
            {errors.position && <div className="field-error">{errors.position}</div>}
          </div>
        </div>

        {/* Row 2: Employment Type and CV */}
        <div className="ast-contact-form-row">
          {/* Employment Type */}
          <div className="form-field">
            <label className="req">Employment Type</label>
            <SearchableSelect
              name="fullpart_time"
              value={formData.fullpart_time || ''}
              onChange={onChange}
              options={[
                { value: 'Full Time', label: 'Full Time' },
                { value: 'Project base / Freelance', label: 'Project base / Freelance' }
              ]}
              placeholder="Please Select"
              error={errors.fullpart_time}
              disabled={loading}
            />
            {errors.fullpart_time && <div className="field-error">{errors.fullpart_time}</div>}
          </div>

          {/* CV Upload */}
          <div className="form-field joinus-file">
            <label className="req">CV</label>
            {!cvFile ? (
              <div>
                <input
                  type="file"
                  name="cv"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="cv-upload"
                  disabled={loading}
                />
                <label
                  htmlFor="cv-upload"
                  style={{
                    display: 'block',
                    padding: '10px 15px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    color: '#8b8d94',
                    fontFamily: '"Neutra Text", Helvetica, Arial, Verdana, sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    borderBottom: `1px solid ${errors.cv ? '#322625' : '#322625'}`,
                    transition: 'all 0.2s ease'
                  }}
                >
                  Allowed Formats: PDF, DOC, DOCX
                </label>
              </div>
            ) : (
              <div
                className="file-preview-container"
                style={{
                  marginTop: 0,
                  padding: '10px 15px',
                  background: 'transparent',
                  borderRadius: 0,
                  border: 'none',
                  borderBottom: '1px solid #322625'
                }}
              >
                <div className="file-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div className="file-name" style={{ fontSize: '15px', fontWeight: 500, color: '#8b8d94', margin: 0 }}>{cvFile.name}</div>
                  {!loading && (
                    <button
                      type="button"
                      className="delete-file"
                      onClick={removeFile}
                      title="Remove file"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#999',
                        cursor: 'pointer',
                        fontSize: '20px',
                        padding: 0,
                        marginLeft: '8px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            )}
            {errors.cv && <div className="field-error">{errors.cv}</div>}
          </div>
        </div>

        {/* Inline Loader */}
        {loading && (
          <div className="inline-loader">
            <div
              className="inline-loader-progress"
              style={{ width: `${progress || 0}%` }}
            />
            <div className="inline-loader-content">
              <div className="inline-loader-message">{loadingMessage || 'Processing your CV...'}</div>
            </div>
          </div>
        )}

        {/* Bottom Note */}
        {/*
        <div style={{
          marginTop: '30px',
          fontSize: '13px',
          color: '#6c757d',
          lineHeight: '1.6'
        }}>
          Upload your CV and we will extract your details. In the next step, you will review and complete any missing fields.
        </div>
        */}

        {/* Next Button */}
        {!loading && (
          <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '40px' }}>
            <button type="button" onClick={handleNext} className="btn btn-primary">
              LET'S GO
            </button>
          </div>
        )}

        {/* reCAPTCHA Notice */}
        <RecaptchaNotice />
      </div>
    </div>
  );
};

export default Tab1_UploadCV;
